/**
 * "Tax bills and returns" + "Documents and requests" — the paperwork categories.
 */

import type { HelpArticle } from '../types';

export const taxBillArticles: HelpArticle[] = [
  {
    slug: 'tax-bills',
    title: 'Tax bills',
    summary: 'The demands for money that arrive once values are settled, and what your firm does with them.',
    category: 'taxbills',
    keywords: ['tax bill', 'bill', 'statement', 'collector', 'due date', 'payment', 'delinquent', 'installment', 'refund'],
    related: ['who-does-what-outside-your-firm', 'transmittals', 'the-tax-year'],
    body: [
      {
        kind: 'text',
        text: 'Once a value is final, the collector works out the tax and sends a bill. Click Tax Bills in the Tax data section to see them.',
      },
      {
        kind: 'text',
        text: 'Many firms handle bills on the client’s behalf: check the bill is right, tell the client what to pay and by when, and chase anything that looks wrong.',
      },
      {
        kind: 'shot',
        src: '/help-shots/tax-bills.png',
        alt: 'The Tax Bills list showing each bill with its parcel, collector, amount, due date and status.',
      },
      {
        kind: 'heading',
        text: 'What to check on a bill',
      },
      {
        kind: 'bullets',
        items: [
          'Does the value on the bill match the value that was agreed? If the reduction did not make it through, the bill is too high and needs correcting.',
          'Is the due date right, and is there an early payment discount worth taking?',
          'Are all the jurisdictions on it the ones that should be?',
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'A bill issued before your win landed will be wrong',
        text: 'Timing means bills sometimes go out on the old value. That is a corrected bill or a refund, not a lost appeal. Chase it rather than assuming the reduction failed.',
      },
    ],
  },

  {
    slug: 'transmittals',
    title: 'Transmittals — sending bills on to clients',
    summary: 'A packaged set of tax bills passed to a client or their lender, with a cover sheet.',
    category: 'taxbills',
    keywords: ['transmittal', 'send bills', 'cover sheet', 'package', 'forward', 'lender', 'escrow', 'deliver'],
    related: ['tax-bills'],
    body: [
      {
        kind: 'text',
        text: 'A transmittal is a bundle of tax bills sent on to whoever is going to pay them — the client, or a lender holding money in escrow for the taxes.',
      },
      {
        kind: 'text',
        text: 'It carries a cover sheet listing what is included and what the totals are, so the recipient can check nothing is missing.',
      },
      {
        kind: 'callout',
        tone: 'note',
        title: 'Transmittal is a status, not a separate list',
        text: 'A transmitted bill is still a tax bill. You will find it by filtering the Tax Bills list on that status rather than on its own menu item.',
      },
    ],
  },

  {
    slug: 'returns',
    title: 'Returns — business personal property',
    summary: 'Some property is declared by the owner each year rather than valued by the assessor. Those declarations are returns.',
    category: 'taxbills',
    keywords: ['return', 'bpp', 'business personal property', 'rendition', 'filing', 'declare', 'equipment', 'inventory', 'fixtures', 'depreciation'],
    related: ['adding-a-parcel', 'deadlines-and-hearings'],
    body: [
      {
        kind: 'text',
        text: 'Business personal property is the movable stuff a business owns — machinery, computers, shelving, stock. In many states the owner has to declare it every year rather than waiting to be told what it is worth. That declaration is a return, sometimes called a rendition.',
      },
      {
        kind: 'text',
        text: 'Click Returns in the Tax data section. Each return moves through its own stages: in progress, prepared, reviewed, filed, and complete.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'Returns have their own deadlines, earlier than appeals',
        text: 'They are usually due well before appeal deadlines, and filing late carries a penalty that is charged as a percentage of the tax. Do not treat return season as the quiet part of the year.',
      },
      {
        kind: 'text',
        text: 'Because equipment loses value as it ages, returns apply depreciation schedules. The app holds those schedules so the declared value drops correctly each year.',
      },
    ],
  },
];

export const docsArticles: HelpArticle[] = [
  {
    slug: 'documents',
    title: 'Documents — storing the paperwork',
    summary: 'Uploading files, attaching them to the right record, and finding them again.',
    category: 'docs',
    keywords: ['document', 'file', 'upload', 'attach', 'pdf', 'scan', 'paperwork', 'evidence', 'storage', 'document type'],
    related: ['document-review-queue', 'information-requests', 'notes-and-activity'],
    body: [
      {
        kind: 'text',
        text: 'Almost everything in this business arrives as paper or a PDF — value notices, board orders, tax bills, leases, rent rolls. Documents keeps them attached to the record they belong to instead of in somebody’s inbox.',
      },
      {
        kind: 'steps',
        intro: 'To upload a file:',
        steps: [
          'Open the record it belongs to — usually a parcel or a client.',
          'Find the Documents panel and click to upload.',
          'Choose the file.',
          'Set the document type. This matters — see below.',
          'Save.',
        ],
      },
      {
        kind: 'shot',
        src: '/help-shots/documents.png',
        alt: 'The Documents list showing uploaded files with their type, the record each is attached to, who uploaded it and when.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'The document type controls who can see it',
        text: 'Some types are automatically shared with the client through their portal. Others are internal. Setting the type wrongly can either hide something the client needs or show them something they should not see. If you are not sure, ask before you save.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'The app can read some documents for you',
        text: 'For common documents like value notices, the app extracts the key numbers automatically and offers them to you. Always check what it extracted against the document before accepting it — it is a time-saver, not an oracle.',
      },
    ],
  },

  {
    slug: 'document-review-queue',
    title: 'The document review queue',
    summary: 'Files waiting for a human to check them and file them properly.',
    category: 'docs',
    keywords: ['review queue', 'document review', 'approve', 'check', 'pending documents', 'unfiled', 'triage', 'assign'],
    related: ['documents', 'information-requests'],
    body: [
      {
        kind: 'text',
        text: 'When documents arrive in bulk — a client sends a folder, or a batch is scanned in — they land in the review queue rather than filing themselves.',
      },
      {
        kind: 'steps',
        intro: 'Working the queue:',
        steps: [
          'Go to Document Review Queue in the Operations section.',
          'Open the first item and look at the file.',
          'Confirm or correct what it is attached to and what type it is.',
          'Accept it, which files it properly, or reject it if it is not something you need.',
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        text: 'This queue is worth clearing regularly. A document nobody has filed is a document nobody can find when the hearing is next week.',
      },
    ],
  },

  {
    slug: 'information-requests',
    title: 'Asking a client for information',
    summary: 'A tracked request for the documents you need, so chasing is not done from memory.',
    category: 'docs',
    keywords: ['information request', 'request', 'ask client', 'chase', 'missing documents', 'rent roll', 'outstanding', 'follow up', 'portal'],
    related: ['documents', 'the-client-portal'],
    body: [
      {
        kind: 'text',
        text: 'To argue a value down you usually need things only the owner has — rent rolls, income statements, photographs, repair invoices. An information request asks for those and keeps track of what has come back.',
      },
      {
        kind: 'steps',
        steps: [
          'Go to Information Requests in the Operations section, or start one from the client.',
          'List the specific items you need. Be precise — "2025 rent roll" is a request somebody can act on, "financials" is not.',
          'Send it. The client sees it in their portal and can upload directly against each item.',
          'Watch the status as items arrive.',
        ],
      },
      {
        kind: 'defs',
        intro: 'What the statuses mean:',
        items: [
          { term: 'Draft', meaning: 'You are still writing it. The client cannot see it.' },
          { term: 'Sent', meaning: 'The client has it. Nothing has come back yet.' },
          { term: 'Partial', meaning: 'Some items arrived, some are still outstanding.' },
          { term: 'Received', meaning: 'Everything you asked for is in.' },
          { term: 'Complete', meaning: 'You have checked it and you are satisfied.' },
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'Received is not the same as complete',
        text: 'Received means files arrived. Complete means you looked at them and they are what you needed. Do not close the request until you have actually opened the documents.',
      },
    ],
  },

  {
    slug: 'forms-library',
    title: 'The forms library',
    summary: 'The official government forms you have to file, and where to find the right one.',
    category: 'docs',
    keywords: ['forms', 'form library', 'template', 'official form', 'state form', 'protest form', 'file a form', 'pdf form'],
    related: ['deadlines-and-hearings', 'returns'],
    body: [
      {
        kind: 'text',
        text: 'Each state, and sometimes each county, has its own official forms for filing a protest or a return. The forms library holds them so nobody is searching a government website at 4pm on a deadline day.',
      },
      {
        kind: 'text',
        text: 'Click Forms Library in the System section. Forms are organized by state and by what they are for.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'Check the year on the form',
        text: 'Governments reissue forms and sometimes reject last year’s version. Confirm you are using the current one before you file.',
      },
    ],
  },
];
